/**
 * backendUrl.js - Configuração Centralizada de URLs do Backend
 * 
 * 🔧 VERSÃO ATUAL: CORREÇÃO CRÍTICA
 * 
 * PROBLEMA: process.env.REACT_APP_BACKEND_URL é definido em BUILD TIME
 * Isso significa que se o .env tem URL de preview, essa URL será usada
 * mesmo em produção, causando erros 404, 502, 520.
 * 
 * SOLUÇÃO: Detectar ambiente em RUNTIME e usar origin do navegador
 * 
 * @usage
 * import { getBackendUrl, getApiUrl } from '../config/backendUrl';
 * const url = getBackendUrl(); // Chamar como função!
 */

// Cache para evitar recálculo
let cachedBackendUrl = null;

/**
 * Obtém URL do backend em RUNTIME (não em build time)
 * @returns {string} URL base do backend
 */
export const getBackendUrl = () => {
  // Retornar cache se já calculado
  if (cachedBackendUrl) {
    return cachedBackendUrl;
  }
  
  // Durante SSR ou build - usar .env como fallback
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Localhost = Desenvolvimento
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    cachedBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return cachedBackendUrl;
  }
  
  // 🔧 Preview/Produção = SEMPRE usar origin atual
  cachedBackendUrl = origin;
  return cachedBackendUrl;
};

/**
 * Obtém URL da API em RUNTIME
 * @returns {string} URL da API (backend + /api)
 */
export const getApiUrl = () => {
  return `${getBackendUrl()}/api`;
};

/**
 * URL base do backend - ATENÇÃO: usar getBackendUrl() em código novo
 * Mantido para compatibilidade com imports existentes
 */
export const BACKEND_URL = typeof window !== 'undefined' ? getBackendUrl() : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

/**
 * URL da API - ATENÇÃO: usar getApiUrl() em código novo
 * Mantido para compatibilidade com imports existentes
 */
export const API_URL = typeof window !== 'undefined' ? `${getBackendUrl()}/api` : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

/**
 * Função utilitária para corrigir URLs incorretas
 * @param {string} url - URL a ser corrigida
 * @returns {string} URL corrigida
 */
export const fixUrl = (url) => {
  if (!url) return url;
  
  const correctBase = getBackendUrl();
  
  const wrongDomains = [
    'https://admin-panel-upgrade-27.preview.emergentagent.com',
    'http://secure-abri.preview.emergentagent.com',
  ];
  
  for (const wrongDomain of wrongDomains) {
    if (url.includes(wrongDomain)) {
      const fixed = url.replace(wrongDomain, correctBase);
      return fixed;
    }
  }
  
  return url;
};

export default { BACKEND_URL, API_URL, getBackendUrl, getApiUrl, fixUrl };
